/**
 * Premium-tide maths for the TIDE panel (plan §13, SPX-009).
 *
 * Framework-agnostic on purpose — no Svelte, no DOM, no fetch — same discipline as `dealer.ts`,
 * and it is what makes these numbers testable without a browser.
 *
 * THE PAYLOAD MIXES TYPES AND YOU MUST NOT ASSUME. Measured off live rows:
 *     { "net_volume": -90234,                  <- NUMBER
 *       "net_put_premium":  "10474027.0000",   <- STRING
 *       "net_call_premium": "36562085.0000",   <- STRING
 *       "timestamp": "2026-08-06T09:30:00-04:00" }
 * Coercion happens once, here, at the boundary.
 *
 * KNOWLEDGE TIME COMES FROM THE SERIES, NOT FROM `vendor_ts`. Measured 6/6 on 08-07: the capture
 * writer stamps `vendor_ts` from `data[0]` — the OLDEST point — so it reads 6h40m older than the
 * newest point inside the very same payload (ledger D-6 / the SPX-009 GO? message). Until SPX-001
 * fixes that, `coverage.endMs` below IS this panel's knowledge time, and it is derived from the
 * data the panel actually draws. That makes the age chip immune to the writer defect rather than
 * dependent on it being fixed.
 *
 * NOTHING HERE IS CALIBRATED. Tide-shift crossings are HYPOTHESIS until the tide STUDY rules
 * (law 6, plan §13 E-studies). This module computes; the panel labels.
 */

/** One element exactly as `/api/market/market-tide` lands it. */
export interface TideRow {
  date: string;
  timestamp: string;
  net_volume: number | string;
  net_put_premium: number | string;
  net_call_premium: number | string;
}

export interface TidePoint {
  /** Epoch ms, parsed from the vendor's own offset-bearing timestamp. */
  tMs: number;
  /** Net call premium, dollars. */
  ncp: number;
  /** Net put premium, dollars. */
  npp: number;
  netVolume: number;
}

export type CrossDirection = "CALL_OVER_PUT" | "PUT_OVER_CALL";

export interface TideCross {
  tMs: number;
  direction: CrossDirection;
}

export interface TideSeries {
  points: TidePoint[];
  /** The window the DATA covers — not when we polled. Empty series ⇒ null. */
  coverage: { startMs: number; endMs: number } | null;
  /** Session date as the vendor labels it (`data[].date`), e.g. "2026-08-06". */
  sessionDate: string | null;
  latest: TidePoint | null;
  /**
   * Call tilt of gross premium: ncp / (|ncp| + |npp|) on the LATEST point.
   *
   * RANGE IS −1..+1, NOT 0..1, and the sign is the informative part: BOTH premiums go negative
   * (measured live 08-06 close — ncp −92.78M, npp −154.81M ⇒ tilt −0.3747). Negative means net
   * call SELLING. An earlier draft of this comment claimed 0..1; the live payload falsified it
   * within minutes, which is why it is stated here rather than assumed.
   *
   * THIS FORMULA IS OURS, NOT THE MOCK'S. The ratified mock renders "BUILD↑ 0.61" against a
   * "threshold 0.55" and publishes no derivation — and since our own measure goes negative on real
   * data, this is explicitly a stated definition, NOT a reconstruction of "BUILD". Logged as a
   * deviation and rendered HYPOTHESIS. Null when gross premium is zero, because a ratio over
   * nothing is undefined, not zero.
   */
  callTilt: number | null;
  /** Sign changes of (ncp − npp). HYPOTHESIS — the tide STUDY has not ruled. */
  crosses: TideCross[];
}

export type TideCursorListener = (tMs: number | null) => void;

export interface TideCursorChannel {
  get(): number | null;
  set(tMs: number | null): void;
  subscribe(listener: TideCursorListener): () => void;
}

/**
 * One timestamp spine for every tide surface. The channel stays framework-agnostic so a cursor
 * move can be kill-tested without a browser and so all five panes consume exactly the same clock.
 */
export function createTideCursorChannel(): TideCursorChannel {
  let value: number | null = null;
  const listeners = new Set<TideCursorListener>();
  return {
    get: () => value,
    set(tMs) {
      if (tMs !== null && !Number.isFinite(tMs)) {
        throw new TypeError("tide cursor timestamp must be finite or null");
      }
      if (Object.is(value, tMs)) return;
      value = tMs;
      for (const listener of listeners) listener(value);
    },
    subscribe(listener) {
      listeners.add(listener);
      listener(value);
      return () => listeners.delete(listener);
    },
  };
}

/** Shared by PREMIUM TIDE and all four expiry-tide panes. */
export const sharedTideCursor = createTideCursorChannel();

/** UTC minute label used by every synchronized tide readout. */
export function formatTideMinute(tMs: number): string {
  if (!Number.isFinite(tMs)) throw new TypeError("tide minute timestamp must be finite");
  const date = new Date(tMs);
  return `${String(date.getUTCHours()).padStart(2, "0")}:${String(date.getUTCMinutes()).padStart(2, "0")}Z`;
}

/** The shared pointer minute is the one clock; the chart supplies its own candle span. */
export function candleForMinute(candleOpensMs: readonly number[], spanMs: number, minuteMs: number): number | null {
  if (!Number.isFinite(minuteMs)) throw new TypeError("tide cursor minute must be finite");
  const validSpan = Number.isFinite(spanMs) && spanMs > 0;
  if (!validSpan) throw new RangeError("tide candle span must be finite and positive");
  for (const open of candleOpensMs) {
    if (!Number.isFinite(open)) throw new TypeError("tide candle open must be finite");
  }
  let lo = 0;
  let hi = candleOpensMs.length - 1;
  let found = -1;
  while (lo <= hi) {
    const mid = (lo + hi) >>> 1;
    if (candleOpensMs[mid] <= minuteMs) {
      found = mid;
      lo = mid + 1;
    } else {
      hi = mid - 1;
    }
  }
  return found >= 0 && minuteMs < candleOpensMs[found] + spanMs ? found : null;
}

/** A follower must never let LWC clamp an offscreen candle onto a viewport edge. */
export function candleIsVisible(index: number, from: number, to: number): boolean {
  if (!Number.isFinite(index)) throw new TypeError("tide candle index must be finite");
  if (!Number.isFinite(from) || !Number.isFinite(to)) {
    throw new TypeError("tide visible logical range must be finite");
  }
  return index >= from && index <= to;
}

export interface TideTimeDomain {
  startMs: number;
  endMs: number;
}

/** Stable 09:30–16:10 America/New_York domain shared by every tide surface. */
export function tideMarketSessionDomain(anchorTMs: number, marketDate: string): TideTimeDomain {
  if (!Number.isFinite(anchorTMs)) throw new TypeError("tide session anchor must be finite");
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(marketDate);
  if (!match) throw new TypeError(`tide market date is invalid: ${JSON.stringify(marketDate)}`);

  const anchorSecond = Math.trunc(anchorTMs / 1000) * 1000;
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: "America/New_York",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hourCycle: "h23",
  }).formatToParts(new Date(anchorSecond));
  const value = (type: Intl.DateTimeFormatPartTypes) => {
    const part = parts.find((candidate) => candidate.type === type)?.value;
    if (part === undefined) throw new TypeError(`tide session could not resolve ${type}`);
    return Number(part);
  };
  const localAnchorAsUtc = Date.UTC(
    value("year"), value("month") - 1, value("day"), value("hour"), value("minute"), value("second"),
  );
  const utcOffsetMs = localAnchorAsUtc - anchorSecond;
  // The route contract accepts historical payloads whose metadata market_date can lag the bar.
  // Geometry follows the timestamp actually drawn; marketDate remains format-validated above.
  const year = value("year");
  const month = value("month") - 1;
  const day = value("day");
  return {
    startMs: Date.UTC(year, month, day, 9, 30) - utcOffsetMs,
    endMs: Date.UTC(year, month, day, 16, 10) - utcOffsetMs,
  };
}

/**
 * Last value at or before the cursor — never nearest-neighbour, because that can borrow a future
 * minute. Input is required to be strictly ascending; feed parsers already enforce that boundary.
 */
export function tideIndexAtOrBefore(timestamps: readonly number[], cursorTMs: number): number | null {
  if (!Number.isFinite(cursorTMs)) throw new TypeError("tide cursor timestamp must be finite");
  let lo = 0;
  let hi = timestamps.length - 1;
  let found = -1;
  while (lo <= hi) {
    const mid = (lo + hi) >>> 1;
    if (timestamps[mid] <= cursorTMs) {
      found = mid;
      lo = mid + 1;
    } else {
      hi = mid - 1;
    }
  }
  return found < 0 ? null : found;
}

/** A6: null means ABSENT; the prior vendor point cannot fill a missing bucket. */
export function tideBucketAt(points: readonly TidePoint[], minuteMs: number, bucketMs: number): number | null {
  if (!Number.isFinite(minuteMs)) throw new TypeError("tide minute must be finite");
  if (!Number.isFinite(bucketMs) || bucketMs <= 0) throw new TypeError("tide bucket must be positive and finite");
  const index = tideIndexAtOrBefore(points.map((point) => point.tMs), minuteMs);
  return index !== null && minuteMs < points[index].tMs + bucketMs ? index : null;
}

import { reportTideScaleRefusal, tideScale } from "./tideScale.ts";

/** One elapsed-time x scale for paths, bars, crossover marks, and the synchronized cursor. */
export function tideTimeX(
  tMs: number,
  startMs: number,
  endMs: number,
  left: number,
  plotWidth: number,
): number {
  const scale = tideScale(startMs, endMs, left, plotWidth);
  if (!scale.ok) {
    reportTideScaleRefusal(scale, "tideTimeX");
    return left;
  }
  if (!Number.isFinite(tMs)) {
    reportTideScaleRefusal({ ok: false, reason: "TIDE_SCALE_UNBOUNDED" }, "tideTimeX");
    return left;
  }
  return scale.x(tMs);
}

/** Exact inverse of tideTimeX, including the honest singleton-session coordinate. */
export function tideTimeAtX(
  x: number,
  startMs: number,
  endMs: number,
  left: number,
  plotWidth: number,
): number {
  const scale = tideScale(startMs, endMs, left, plotWidth);
  if (!scale.ok) {
    reportTideScaleRefusal(scale, "tideTimeAtX");
    return startMs;
  }
  if (!Number.isFinite(x)) {
    reportTideScaleRefusal({ ok: false, reason: "TIDE_SCALE_UNBOUNDED" }, "tideTimeAtX");
    return startMs;
  }
  return scale.tAt(x);
}

function num(v: number | string | undefined | null, field: string): number {
  const n = typeof v === "number" ? v : Number(v);
  if (!Number.isFinite(n)) throw new TypeError(`tide: ${field} is not numeric: ${JSON.stringify(v)}`);
  return n;
}

/**
 * Parse and sort. The vendor sends this series ascending, but `/api/stock/SPY/ohlc/1m` on the very
 * same feed arrives DESCENDING — so ordering is not a property of the vendor, and assuming it here
 * would make the coverage window and the last-value read silently wrong on a feed change.
 */
export function parseTide(rows: TideRow[]): TidePoint[] {
  const pts = rows.map((r, i) => {
    const tMs = Date.parse(r.timestamp);
    if (!Number.isFinite(tMs)) {
      throw new TypeError(`tide: row ${i} timestamp is not parseable: ${JSON.stringify(r.timestamp)}`);
    }
    return {
      tMs,
      ncp: num(r.net_call_premium, `row ${i} net_call_premium`),
      npp: num(r.net_put_premium, `row ${i} net_put_premium`),
      netVolume: num(r.net_volume, `row ${i} net_volume`),
    };
  });
  return pts.sort((a, b) => a.tMs - b.tMs);
}

/** Sign changes of (ncp − npp) across consecutive points. A touch of exactly 0 is not a cross. */
export function tideCrosses(points: TidePoint[]): TideCross[] {
  const out: TideCross[] = [];
  for (let i = 1; i < points.length; i++) {
    const prev = points[i - 1].ncp - points[i - 1].npp;
    const curr = points[i].ncp - points[i].npp;
    if (prev === 0 || curr === 0) continue;
    if (Math.sign(prev) !== Math.sign(curr)) {
      out.push({ tMs: points[i].tMs, direction: curr > 0 ? "CALL_OVER_PUT" : "PUT_OVER_CALL" });
    }
  }
  return out;
}

export function callTilt(p: TidePoint | null): number | null {
  if (!p) return null;
  const gross = Math.abs(p.ncp) + Math.abs(p.npp);
  if (gross === 0) return null;
  return p.ncp / gross;
}

export function buildTide(rows: TideRow[]): TideSeries {
  const points = parseTide(rows);
  const latest = points.length ? points[points.length - 1] : null;
  return {
    points,
    coverage: points.length ? { startMs: points[0].tMs, endMs: points[points.length - 1].tMs } : null,
    sessionDate: rows.length ? (rows[rows.length - 1].date ?? null) : null,
    latest,
    callTilt: callTilt(latest),
    crosses: tideCrosses(points),
  };
}

/**
 * Seconds between the newest DATA point and `nowMs` — the honest "how old is this" for a panel,
 * as distinct from poll age. Returns null on an empty series rather than 0: "no data" and "data
 * from this instant" are different claims and a chip must never conflate them.
 */
export function dataAgeS(series: TideSeries, nowMs: number): number | null {
  if (!series.coverage) return null;
  if (series.coverage.endMs > nowMs) return null;
  return Math.round((nowMs - series.coverage.endMs) / 1000);
}

export type DataAgeState = { state: "NO_DATA" }
  | { state: "FUTURE_DATED"; aheadS: number }
  | { state: "AGED"; ageS: number };

/** Name an impossible future coverage end so callers can render a refusal instead of fresh ink. */
export function dataAgeState(series: TideSeries, nowMs: number): DataAgeState {
  if (!series.coverage) return { state: "NO_DATA" };
  if (series.coverage.endMs > nowMs) {
    return { state: "FUTURE_DATED", aheadS: Math.round((series.coverage.endMs - nowMs) / 1000) };
  }
  return { state: "AGED", ageS: dataAgeS(series, nowMs)! };
}

/** A spot bar from `/api/stock/SPY/ohlc/1m`, for the optional overlay. */
export interface SpotBar {
  start_time: string;
  end_time: string;
  close: number | string;
}

export interface SpotPoint {
  tMs: number;
  close: number;
}

export function parseSpot(bars: SpotBar[]): SpotPoint[] {
  return bars
    .map((b, i) => {
      const tMs = Date.parse(b.end_time);
      if (!Number.isFinite(tMs)) {
        throw new TypeError(`tide: spot bar ${i} end_time is not parseable: ${JSON.stringify(b.end_time)}`);
      }
      return { tMs, close: num(b.close, `spot bar ${i} close`) };
    })
    .sort((a, b) => a.tMs - b.tMs);
}

/**
 * Align spot to the tide grid, POINT-IN-TIME (law 3): each tide point takes the last spot bar at or
 * BEFORE it, never the nearest and never a later one. Nearest-neighbour would let a bar from the
 * future decide a past point, which is the reconstruct-after-the-fact error law 3 exists to forbid.
 *
 * Returns null per tide point where no prior spot bar exists, so the caller renders a gap instead of
 * inventing a value — an absent overlay must read as absent.
 */
export function alignSpot(points: TidePoint[], spot: SpotPoint[]): (number | null)[] {
  const out: (number | null)[] = [];
  let i = 0;
  let last: number | null = null;
  for (const p of points) {
    while (i < spot.length && spot[i].tMs <= p.tMs) {
      last = spot[i].close;
      i++;
    }
    out.push(last);
  }
  return out;
}
